import React, {useEffect, useRef, useState} from 'react';

import {withStyles} from 'common/util/react';
import {react2panel} from 'common/util/react2panel';
import {useTranslation} from 'common/i18n/hooks';
import {SpinnerReact} from 'common/components/spinner';
import {SpinnerSize} from 'common/components/spinner/types';

import {RecordingEventMetadata, RecordingMetadata} from './types';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import {REPLAY_EVENT} from './query-utils';
import {formatRecordingEventTime, getSearchHighlightPieces} from './util';

type ReplayPlaylistRowEventMetadataProps = {
  loadingEventMetadata: boolean;
  replay: RecordingMetadata;
  replayEvents: RecordingEventMetadata[];
  jumpToEvent: (time: number, replayId: string) => void;
  currentRecordingProgressTime: number;
  searchFilter: string;
};

function replayPlaylistRowEventMetadata(props: ReplayPlaylistRowEventMetadataProps) {
  const [userInteracting, setUserInteracting] = useState(false);

  const eventMetadataContainerRef = useRef<HTMLDivElement>(null);
  const selectedEventRowRef = useRef<HTMLDivElement>(null);

  const {t} = useTranslation();

  useEffect(() => {
    if (
      props.currentRecordingProgressTime &&
      props.currentRecordingProgressTime < props.replay.time + props.replay.duration / 1000 &&
      props.currentRecordingProgressTime > props.replay.time
    ) {
      scrollContainerToSelectedEvent();
    }
  }, [selectedEventRowRef.current]);

  const getSelectedEventIndex = (sortedEventMetadata: RecordingEventMetadata[]) => {
    if (!props.currentRecordingProgressTime) {
      return -1;
    }

    let minDistance = Number.POSITIVE_INFINITY;
    let selectedIndex = -1;

    sortedEventMetadata.forEach((event, i) => {
      const distance = Math.abs(event.eventTime - props.currentRecordingProgressTime);
      if (distance < minDistance) {
        selectedIndex = i;
        minDistance = distance;
      }
    });

    return selectedIndex;
  };

  function scrollContainerToSelectedEvent() {
    if (!userInteracting && !props.searchFilter && selectedEventRowRef.current && eventMetadataContainerRef.current) {
      const scrollDistance =
        selectedEventRowRef.current.getBoundingClientRect().top -
        eventMetadataContainerRef.current.getBoundingClientRect().top;

      eventMetadataContainerRef.current.scrollBy({top: scrollDistance, behavior: `smooth`});
    }
  }

  function maybeShowSearchHighlight(inputText: string) {
    const pieces = getSearchHighlightPieces(inputText, props.searchFilter);

    return (
      <>
        {pieces.map((piece, i) => {
          return (
            <span className={piece.highlight ? styles.replaysTextHighlight : ``} key={i}>
              {piece.text}
            </span>
          );
        })}
      </>
    );
  }

  const existingEventMetadata = props.replayEvents || [];

  let sortedEventMetadata = existingEventMetadata
    .sort((a, b) => a.eventTime - b.eventTime)
    .filter((event) => event.eventName !== REPLAY_EVENT);

  if (props.searchFilter) {
    sortedEventMetadata = sortedEventMetadata.filter((event) => {
      return getSearchHighlightPieces(event.eventName, props.searchFilter).some((piece) => piece.highlight);
    });
  }

  const selectedEventIndex = getSelectedEventIndex(sortedEventMetadata);

  return (
    <div
      className={`${styles.replaysListItemEventExpansion} ${!props.currentRecordingProgressTime && styles.replaysListItemEventExpansionBordered}`}
      ref={eventMetadataContainerRef}
      onMouseOver={() => {
        setUserInteracting(true);
      }}
      onMouseLeave={() => {
        setUserInteracting(false);
      }}
      role={`rowgroup`}
    >
      {props.loadingEventMetadata && existingEventMetadata.length === 0 ? (
        <SpinnerReact className={styles.loadingSpinner} size={SpinnerSize.Small} role={`progressbar`} />
      ) : (
        <div className={styles.replaysListItemEventBody}>
          {sortedEventMetadata.length === 0 && (
            <div className={styles.replaysListItemEventHeaderEmpty}>{t(`session-replay.no-related-events`)}</div>
          )}
          {sortedEventMetadata.map((event, i) => {
            return (
              <div
                key={i}
                className={`${styles.replaysListItemEvent} ${
                  selectedEventIndex === i && styles.replaysListItemEventTimeSelected
                }`}
                onClick={() => {
                  props.jumpToEvent(event.eventTime, props.replay.replayId);
                }}
                ref={selectedEventIndex === i ? selectedEventRowRef : null}
                role={`row`}
              >
                <div className={styles.replaysListItemEventFirst}>
                  <div className={styles.replaysListItemEventTime}>
                    {formatRecordingEventTime(props.replay.time, event.eventTime)}
                  </div>
                  <div className={styles.replaysListItemEventName}>{maybeShowSearchHighlight(event.eventName)}</div>
                </div>
                <div className={styles.replaysListItemEventQuantity}>{event.quantity}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ReplayPlaylistRowEventMetadataStyled = withStyles(replayPlaylistRowEventMetadata, css);
export const ReplayPlaylistRowEventMetadataEl = react2panel(
  `mp-replay-playlist-row-event-metadata`,
  ReplayPlaylistRowEventMetadataStyled,
);
